import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { startOfMonth, endOfMonth, subDays, subMonths, isValid } from 'date-fns'
import { parseDDMMYYYY, toIsoDate } from '@/lib/utils'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'

const DEFAULT_BOUNDARY = 5

export type RangePresetId =
  | 'today'
  | 'yesterday'
  | 'last_7'
  | 'last_30'
  | 'this_month'
  | 'last_month'
  | 'custom'

export interface ReportRange {
  /** Inclusive local start, midnight Asia/Karachi (the same calendar day the user picked). */
  from: Date
  /** Inclusive local end (calendar day). */
  to: Date
  /** Stable preset identifier for the active selection. */
  preset: RangePresetId
}

export interface UseReportRange extends ReportRange {
  /** ISO YYYY-MM-DD — what we send to the backend. */
  fromISO: string
  toISO: string
  /** Total inclusive day count (1 for a single day). */
  days: number
  setRange: (next: { from: Date; to: Date; preset?: RangePresetId }) => void
  setPreset: (preset: RangePresetId) => void
}

/**
 * Returns the current business date, mirroring the backend's
 * `util.BusinessDate(util.BusinessNow())`. The day boundary hour is
 * subtracted from the current wall-clock time before truncating to
 * midnight, so e.g. at 4:48 AM with boundary=5 the business date is
 * still the previous calendar day.
 */
function businessToday(boundaryHour: number = DEFAULT_BOUNDARY): Date {
  const shifted = new Date(Date.now() - boundaryHour * 3_600_000)
  shifted.setHours(0, 0, 0, 0)
  return shifted
}

/**
 * Given a preset, returns the [from, to] interval in local calendar days.
 * Custom returns null — it must come from URL or user picker.
 *
 * `boundaryHour` shifts "today" to match the backend's business-day
 * definition so the Reports "Today" / "Yesterday" presets agree with the
 * Expense Dashboard and Day Close surfaces.
 */
export function rangeForPreset(
  preset: RangePresetId,
  boundaryHour: number = DEFAULT_BOUNDARY,
): { from: Date; to: Date } | null {
  const today = businessToday(boundaryHour)
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday': {
      const y = subDays(today, 1)
      return { from: y, to: y }
    }
    case 'last_7':
      return { from: subDays(today, 6), to: today }
    case 'last_30':
      return { from: subDays(today, 29), to: today }
    case 'this_month':
      return { from: startOfMonth(today), to: endOfMonth(today) > today ? today : endOfMonth(today) }
    case 'last_month': {
      const m = subMonths(today, 1)
      return { from: startOfMonth(m), to: endOfMonth(m) }
    }
    case 'custom':
    default:
      return null
  }
}

const PRESET_VALUES: RangePresetId[] = [
  'today',
  'yesterday',
  'last_7',
  'last_30',
  'this_month',
  'last_month',
  'custom',
]

function readSearch(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams()
  return new URLSearchParams(window.location.search)
}

function writeSearch(params: URLSearchParams) {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`
  window.history.replaceState(null, '', next)
}

function parseInitialRange(
  boundaryHour: number = DEFAULT_BOUNDARY,
  defaultPreset: RangePresetId = 'today',
): ReportRange {
  const params = readSearch()
  const presetParam = params.get('preset') as RangePresetId | null
  const fromParam = params.get('from')
  const toParam = params.get('to')

  if (fromParam && toParam) {
    const f = parseDDMMYYYY(fromParam)
    const t = parseDDMMYYYY(toParam)
    if (f && t && isValid(f) && isValid(t) && f <= t) {
      return {
        from: f,
        to: t,
        preset: presetParam && PRESET_VALUES.includes(presetParam) ? presetParam : 'custom',
      }
    }
  }

  if (presetParam && PRESET_VALUES.includes(presetParam) && presetParam !== 'custom') {
    const r = rangeForPreset(presetParam, boundaryHour)
    if (r) return { ...r, preset: presetParam }
  }

  const fallback = rangeForPreset(defaultPreset, boundaryHour)
  if (fallback) return { ...fallback, preset: defaultPreset }
  const today = businessToday(boundaryHour)
  return { from: today, to: today, preset: 'today' }
}

/**
 * Single source of truth for the date range used across the Reports page.
 * State is mirrored to URL search params (`from`, `to`, `preset`) using the
 * project's DD-MM-YYYY convention so deep links remain human-readable.
 *
 * The hook now reads the server's `day_boundary_hour` setting so that
 * presets like "Today" and "Yesterday" align with the backend's business-day
 * definition — the root cause of the old Reports-vs-Expense gross mismatch.
 */
export function useReportRange(defaultPreset: RangePresetId = 'today'): UseReportRange {
  const boundaryHour = useDayBoundaryHour()
  const [state, setState] = useState<ReportRange>(() => parseInitialRange(DEFAULT_BOUNDARY, defaultPreset))

  // Re-compute preset dates when the real boundary hour arrives from the
  // server (the lazy initializer above uses the compile-time default).
  const prevBoundary = useRef(DEFAULT_BOUNDARY)
  useEffect(() => {
    if (boundaryHour === prevBoundary.current) return
    prevBoundary.current = boundaryHour
    if (state.preset !== 'custom') {
      const r = rangeForPreset(state.preset, boundaryHour)
      if (r) setState({ ...r, preset: state.preset })
    }
  }, [boundaryHour, state.preset])

  useEffect(() => {
    const params = readSearch()
    params.set('from', toIsoToHumanDDMMYYYY(state.from))
    params.set('to', toIsoToHumanDDMMYYYY(state.to))
    params.set('preset', state.preset)
    writeSearch(params)
  }, [state])

  const setRange = useCallback(
    ({ from, to, preset = 'custom' }: { from: Date; to: Date; preset?: RangePresetId }) => {
      const ordered = from <= to ? { from, to } : { from: to, to: from }
      setState({ ...ordered, preset })
    },
    [],
  )

  const setPreset = useCallback((preset: RangePresetId) => {
    if (preset === 'custom') {
      setState((prev) => ({ ...prev, preset }))
      return
    }
    const r = rangeForPreset(preset, boundaryHour)
    if (r) setState({ ...r, preset })
  }, [boundaryHour])

  return useMemo(() => {
    const fromISO = toIsoDate(state.from)
    const toISO = toIsoDate(state.to)
    const ms = state.to.getTime() - state.from.getTime()
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
    return {
      ...state,
      fromISO,
      toISO,
      days,
      setRange,
      setPreset,
    }
  }, [state, setRange, setPreset])
}

// --- helpers --------------------------------------------------------------

/**
 * The URL form of a range — exactly what parseInitialRange reads back — so a
 * tile on one page can deep-link into another page's period bar
 * (Reports → Expenses, Expenses → Reports Items). F-FE-N129.
 */
export function rangeToSearch(r: ReportRange): { preset: RangePresetId; from: string; to: string } {
  return { preset: r.preset, from: toIsoToHumanDDMMYYYY(r.from), to: toIsoToHumanDDMMYYYY(r.to) }
}

// Local-only helper that mirrors lib/utils.formatDateDDMMYYYY without importing
// it as a value (avoids a cyclic dep risk between hooks and lib).
function toIsoToHumanDDMMYYYY(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0')
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const yr = String(d.getFullYear())
  return `${day}-${mo}-${yr}`
}
